import { useContext, RefObject } from 'react'

import { DragNDropStateContext, ToggleDragDrops } from '../drag-n-drop-provider'
import useDragNDropListeners from './use-drag-n-drop-listeners'
import shouldAcceptDrop from '../should-accept-drop'

export type DragNDropReturn = {
  isDraggable: boolean
  isDragged: boolean
  isEntered: boolean
  isEnterable: boolean
  someDragIsActive: boolean
  someElementIsEntered: boolean
  enabledDragGroupIds: string[]
  currentlyDraggingDragGroupIds: string[]
  toggleDragDrops: ToggleDragDrops
}

/**
 * The **useDragNDrop** hook may be used in a descendant of **DragNDropProvider**
 *
 * Use this hook when making an element draggable and/or making it a dropzone
 * - Automatically manages event listener bindings if an element's reference is provided
 * - Returns a summary of drag group related dragging/entering related information that may be particularly relevant
 *
 * This hook can also generally be used to determine whether an element of certain drag groups
 *   is being dragged or entering a dropzone or to toggle/set whether a drag group is enabled or not
 *   (just provide null as the elemRef if there's no element that you are associating with a drag group/dropzone)
 *
 * @param elemRef
 * Ref for an HTMLElement that should have the returned draggable property added as appropriate
 * @param dragGroupIds
 * Array of drag group ids that should:
 * - determine if dragging the element is enabled (if any of the drag groups are enabled, dragging should be enabled)
 * - determine which dropzones will accept the element
 * @param dropzoneDragGroupIds
 * Array of drag group ids that should:
 * - activate this element as a dropzone when the element being dragged is associated with the drag group and the drag group is enabled
 *
 * @returns
 *  Returns a summary object of dragging/entering-related information for the drag groups that may be particularly relevant
 *   and a helper for setting/toggling whether the drag group is enabled
 *
 * ### Returned summary object
 * #### isDraggable
 *     Answers the question "Can the draggable property for this element be set to true based on the enabled drag groups?"
 *
 * #### isDragged
 *     Answers the question "Is this element currently being dragged?"
 *
 * #### isEntered
 *     Answers the question "Is this element currently being entered as a dropzone by an element that is being dragged?"
 *
 * #### isEnterable
 *     Answers the question "Is this element a candidate dropzone for an element that is currently being dragged?"
 *
 * #### someDragIsActive
 *     Answers the question "Is some element in the specified drag groups currently being dragged?"
 *
 * #### someElementIsEntered
 *     Answers the question "Is some candidate dropzone being entered by an element that is currently being dragged?"
 *
 * ### enabledDragGroupIds
 *    Answers the question "What drag group ids are enabled (if any)"
 *
 * #### currentlyDraggingDragGroupIds
 *    Answers the question "What drag group ids are associated with the element currently being dragged (if any)"
 *
 * ### toggleDragDrops
 *     Allows setting/toggling whether a drag group is enabled for dragging
 *
 * */
export const useDragNDrop = (
  elemRef: RefObject<HTMLElement | null> | null,
  dragGroupIds: string[],
  dropzoneDragGroupIds: (string | string[])[],
): DragNDropReturn => {
  const dragNDropContext = useContext(DragNDropStateContext)
  const { getListenerMapping } = dragNDropContext ?? {}
  const elem = elemRef?.current ?? null
  useDragNDropListeners(
    elem,
    getListenerMapping?.(dragGroupIds, dropzoneDragGroupIds) ?? {},
  )
  if (dragNDropContext === undefined) {
    throw new Error(
      'The context-based React hook, useDragNDrop, must have DragNDropProvider as an ancestor!',
    )
  }
  const {
    isDraggingEnabled,
    activelyDraggedElem,
    activelyEnteredElem,
    enabledDragGroupIds,
    currentlyDraggingDragGroupIds,
    toggleDragDrops,
  } = dragNDropContext

  const activatedDropzoneDragGroupIds = (
    currentlyDraggingDragGroupIds ?? []
  ).filter((groupId) => (enabledDragGroupIds ?? []).includes(groupId))
  return {
    isDraggable: !!isDraggingEnabled?.(dragGroupIds),
    isDragged: !!elem && elem === activelyDraggedElem,
    isEntered: !!elem && elem === activelyEnteredElem,
    isEnterable: !!(
      elem &&
      dropzoneDragGroupIds.some((dragGroupIdOrIds) =>
        shouldAcceptDrop(dragGroupIdOrIds, activatedDropzoneDragGroupIds),
      )
    ),
    someDragIsActive: !!activelyDraggedElem,
    someElementIsEntered: !!activelyEnteredElem,
    enabledDragGroupIds,
    currentlyDraggingDragGroupIds,
    toggleDragDrops,
  }
}
